// @ts-ignore
import {
  AcceptMessage,
  Input,
  OfferMessage,
  SignMessage,
} from '@atomicfinance/bitcoin-dlc-provider';
import {
  Client as FinanceClient,
  providers as dlcProviders,
} from '@atomicfinance/bundle';
import BitcoinEsploraApiProvider from '@liquality/bitcoin-esplora-api-provider';
import BitcoinJsWalletProvider from '@liquality/bitcoin-js-wallet-provider';
import BitcoinNetworks from '@liquality/bitcoin-networks';
import { decodeRawTransaction, selectCoins } from '@liquality/bitcoin-utils';
import Client from '@liquality/client';
import BigNumber from 'bignumber.js';
import bitcoin from 'bitcoinjs-lib';
import { CreateKeyPairRequest, DecodeRawTransactionRequest } from 'cfd-js-wasm';
import config from '../config/config';

const clients: { [mnemonic: string]: Bitcoin } = {};
const bitcoinNetwork = BitcoinNetworks[config.bitcoin.network];

const ADDRESS_GAP = 20;

export class Bitcoin {
  public client: typeof Client;
  private network;

  constructor(network, mnemonic) {
    const bitcoin = new Client();
    const bitcoinFinance = new FinanceClient(bitcoin);

    bitcoin.finance = bitcoinFinance;
    bitcoin.addProvider(new BitcoinEsploraApiProvider(config.bitcoin.api));
    bitcoin.addProvider(
      new BitcoinJsWalletProvider(network, mnemonic, 'bech32')
    );

    const cfdProvider = new dlcProviders.bitcoin.BitcoinCfdProvider(network);
    const dlcProvider = new dlcProviders.bitcoin.BitcoinDlcProvider(network);
    const walletProvider = new dlcProviders.bitcoin.BitcoinWalletProvider(
      network
    );

    bitcoin.finance.addProvider(cfdProvider);
    bitcoin.finance.addProvider(dlcProvider);
    bitcoin.finance.addProvider(walletProvider);

    this.client = bitcoin;
    this.network = network;
  }

  createKeyPair() {
    const reqJson: CreateKeyPairRequest = {
      wif: false,
    };
    return this.client.finance.cfd.CreateKeyPair(reqJson);
  }

  getPubkeyFromPrivkey(privkey: string) {
    const reqPrivKey = {
      privkey,
      isCompressed: true,
    };

    return this.client.finance.cfd.GetPubkeyFromPrivkey(reqPrivKey).pubkey;
  }

  getPrivkeyFromWif(wif: string) {
    const req = {
      wif,
    };
    return this.client.finance.cfd.GetPrivkeyFromWif(req).hex;
  }

  decodeRawTransaction(rawTransaction: string) {
    const reqJson: DecodeRawTransactionRequest = {
      hex: rawTransaction,
    };
    return this.client.finance.cfd.DecodeRawTransaction(reqJson);
  }

  async initializeContractAndOffer(
    inputDetails,
    outcomes,
    oracleInfo,
    startingIndex: number,
    fixedInputs: Input[]
  ) {
    return await this.client.finance.dlc.initializeContractAndOffer(
      inputDetails,
      outcomes,
      oracleInfo,
      startingIndex,
      fixedInputs
    );
  }

  async getInputsForAmount(
    _targets: any = [],
    ignoredInputs: any = [],
    _feePerByte?,
    fixedInputs: any = [],
    numAddressPerCall = 100,
    sweep = false
  ) {
    const bitcoin = this;
    let addressIndex = 0;
    let changeAddresses = [];
    let nonChangeAddresses = [];
    let addressCountMap = {
      change: 0,
      nonChange: 0,
    };

    let feePerByte = _feePerByte || false;

    while (
      addressCountMap.change < ADDRESS_GAP ||
      addressCountMap.nonChange < ADDRESS_GAP
    ) {
      let addrList: any[] = [];

      if (addressCountMap.change < ADDRESS_GAP) {
        // Scanning for change addr
        changeAddresses = await bitcoin.getMethod('getAddresses')(
          addressIndex,
          numAddressPerCall,
          true
        );
        addrList = addrList.concat(changeAddresses);
      } else {
        changeAddresses = [];
      }

      if (addressCountMap.nonChange < ADDRESS_GAP) {
        // Scanning for non change addr
        nonChangeAddresses = await bitcoin.getMethod('getAddresses')(
          addressIndex,
          numAddressPerCall,
          false
        );
        addrList = addrList.concat(nonChangeAddresses);
      }

      let utxos: any[];
      if (sweep === false || fixedInputs.length === 0) {
        utxos = await bitcoin.getMethod('getUnspentTransactions')(addrList);
        if (ignoredInputs)
          utxos = utxos.filter((utxo) =>
            ignoredInputs.every(
              (ignoredInput) => ignoredInput.txid !== utxo.txid
            )
          );
        utxos = utxos.map((utxo) => {
          const addr = addrList.find((a) => a.equals(utxo.address));
          return {
            ...utxo,
            value: new BigNumber(utxo.amount).times(1e8).toNumber(),
            derivationPath: addr.derivationPath,
          };
        });
      } else {
        utxos = fixedInputs;
      }

      const utxoBalance = utxos.reduce((a, b) => a + (b['value'] || 0), 0);

      const transactionCounts = await bitcoin.getMethod(
        'getAddressTransactionCounts'
      )(addrList);

      const minRelayFee = await bitcoin.getMethod('getMinRelayFee')();
      if (feePerByte < minRelayFee) {
        throw new Error(
          `Fee supplied (${feePerByte} sat/b) too low. Minimum relay fee is ${minRelayFee} sat/b`
        );
      }

      if (fixedInputs.length) {
        for (const input of fixedInputs) {
          const txHex = await bitcoin.getMethod('getRawTransactionByHash')(
            input.txid
          );
          const tx = decodeRawTransaction(
            txHex,
            await bitcoin.getMethod('getConnectedNetwork')()
          );
          input.value = new BigNumber(tx.vout[input.vout].value)
            .times(1e8)
            .toNumber();
          input.address = tx.vout[input.vout].scriptPubKey.addresses[0];
          const walletAddress = await bitcoin.getMethod('getWalletAddress')(
            input.address
          );
          input.derivationPath = walletAddress.derivationPath;
        }
      }

      let targets: any[];
      if (sweep) {
        const outputBalance = _targets.reduce(
          (a, b) => a + (b['value'] || 0),
          0
        );

        const amountToSend =
          utxoBalance -
          feePerByte * ((_targets.length + 1) * 39 + utxos.length * 153); // todo better calculation

        targets = _targets.map((target, i) => ({
          id: 'main',
          value: target.value,
        }));
        targets.push({ id: 'main', value: amountToSend - outputBalance });
      } else {
        targets = _targets.map((target, i) => ({
          id: 'main',
          value: target.value,
        }));
      }

      const { inputs, outputs, fee } = selectCoins(
        utxos,
        targets,
        Math.ceil(feePerByte),
        fixedInputs
      );

      if (inputs && outputs) {
        let change = outputs.find((output) => output.id !== 'main');

        if (change && change.length) {
          change = change[0].value;
        }

        return {
          inputs,
          change,
          outputs,
          fee,
        };
      }

      for (let address of addrList) {
        const isUsed = transactionCounts[address.address];
        const isChangeAddress = changeAddresses.find((a) => address.equals(a));
        const key = isChangeAddress ? 'change' : 'nonChange';

        if (isUsed) {
          addressCountMap[key] = 0;
        } else {
          addressCountMap[key]++;
        }
      }

      addressIndex += numAddressPerCall;
    }

    throw new Error('Not enough balance');
  }

  async confirmContractOffer(
    offerMessage: OfferMessage,
    startingIndex: number,
    fixedInputs: Input[]
  ) {
    return await this.client.finance.dlc.confirmContractOffer(
      offerMessage,
      startingIndex,
      fixedInputs
    );
  }

  async signContract(acceptMessage: AcceptMessage) {
    return await this.client.finance.dlc.signContract(acceptMessage);
  }

  async finalizeContract(signMessage: SignMessage) {
    return await this.client.finance.dlc.finalizeContract(signMessage);
  }

  async unilateralClose(
    oracleSignature: string,
    outcomeIndex: number,
    contractId: string
  ) {
    return await this.client.finance.dlc.unilateralClose(
      oracleSignature,
      outcomeIndex,
      contractId
    );
  }

  isValidAddress(addr) {
    try {
      bitcoin.address.toOutputScript(addr, this.network);
      return true;
    } catch (e) {
      return false;
    }
  }

  hasContract(contractId: string) {
    return this.client.finance.dlc.hasDlc(contractId);
  }

  exportContract(contractId: string) {
    return this.client.finance.dlc.exportContract(contractId);
  }

  getMethod(method: string) {
    return this.client.getMethod(method);
  }
}

export const createClient = (mnemonic) => {
  if (clients[bitcoinNetwork]) return clients[bitcoinNetwork];
  clients[bitcoinNetwork] = new Bitcoin(bitcoinNetwork, mnemonic);
  return clients[bitcoinNetwork];
};

export const getClient = () => {
  if (!clients[bitcoinNetwork]) throw new Error('Client not initialized');
  return clients[bitcoinNetwork];
};
